import { useMemo } from 'react';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash-es';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { WivPromotionCard, useWivTeaserHandler } from '@frontend/self-upgrade';
import { theme } from '@frontend/theme';
import { NakedButton, SpinningLoader } from '@frontend/design-system';
import { useGetLocationVerticalForPayerList } from '../../hooks/use-get-location-vertical-for-payer-list';
import { INSURANCE_STATUS_MAPPING, unverifiedStatus } from '../../utils/constants';
import { InsuranceStatusChip } from './../insurance-status-chip';

interface Props {
  personId: string;
  locationId: string;
  onClick: () => void;
  handleVerifyStatusClick?: () => void;
  isLoading?: boolean;
  shouldShowVerifyButton?: boolean;
}

export const InsuranceStatusChipButton = ({
  personId,
  locationId,
  onClick,
  handleVerifyStatusClick,
  isLoading = false,
  shouldShowVerifyButton = true,
}: Props) => {
  const { isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(locationId);

  const {
    isLoading: isLoadingWivEligibility,
    showWeaveVerify,
    showWeaveVerifyPromotion,
  } = useWivTeaserHandler({ locationIds: [locationId] });

  const { data: verificationDetailsForDental, isLoading: isLoadingVerificationDetails } =
    InsuranceDetailQueries.useGetDentalInsuranceVerificationDetailsPersonId({
      personId,
      locationId,
      options: {
        enabled: isDental && showWeaveVerify,
      },
    });

  const { data: weaveVerificationDetails, isLoading: isLoadingWeaveVerificationDetails } =
    InsuranceDetailQueries.useGetNonDentalInsuranceVerificationDetailsByPersonId({
      personId,
      locationId,
      options: {
        enabled: !isDental && isWellnessVertical && showWeaveVerify,
      },
    });

  const verificationDetails = isDental ? verificationDetailsForDental : weaveVerificationDetails;

  const statusInfo = useMemo(() => {
    const status =
      verificationDetails?.coverage?.thirdPartyCoverage?.verificationStatus ||
      verificationDetails?.coverage?.thirdPartyCoverage?.coverage?.status;
    const overriddenStatus = verificationDetails?.coverage?.overriddenCoverage?.coverage;
    const hasError = !isEmpty(verificationDetails?.coverage?.thirdPartyCoverage?.errorDetails);

    const statusValue = overriddenStatus
      ? INSURANCE_STATUS_MAPPING.get(overriddenStatus.toLowerCase().split('_').join('-'))
      : status
      ? INSURANCE_STATUS_MAPPING.get(status.toLowerCase())
      : hasError
      ? INSURANCE_STATUS_MAPPING.get('error')
      : undefined;

    return statusValue;
  }, [
    verificationDetails?.coverage?.thirdPartyCoverage?.coverage?.status,
    verificationDetails?.coverage?.thirdPartyCoverage?.verificationStatus,
    verificationDetails?.coverage?.overriddenCoverage?.coverage,
    verificationDetails?.coverage?.thirdPartyCoverage?.errorDetails,
  ]);

  const handleInsuranceStatusClick = () => {
    const hasError = !isEmpty(verificationDetails?.coverage?.thirdPartyCoverage?.errorDetails);
    const hasInsuranceVerificationData = !!verificationDetails?.coverage?.thirdPartyCoverage?.coverage;
    const shouldShowInsuranceTabOnClick = handleVerifyStatusClick && (!hasInsuranceVerificationData || hasError);
    // If there is an error or no insurance verification data, we will open the insurance tab along with insurance form in the side panel.
    // onClick method will open the insurance tab
    // handleVerifyStatusClick will open the insurance verification form side panel.clicking on the chip as the side panel is more of an action related to insurance verification rather than just viewing the insurance details.
    onClick();
    if (shouldShowInsuranceTabOnClick) {
      handleVerifyStatusClick();
    }
  };

  if (isLoading || isLoadingVerificationDetails || isLoadingWivEligibility || isLoadingWeaveVerificationDetails)
    return <SpinningLoader size='xs' />;

  if (!showWeaveVerify && !showWeaveVerifyPromotion) return null;

  if (!statusInfo && shouldShowVerifyButton) {
    return (
      <div css={[{ display: 'inline-flex' }, showWeaveVerifyPromotion && wivPromotionWrapperStyles]}>
        <VerifyInsuranceButton showWeaveVerify={showWeaveVerify} onClick={handleInsuranceStatusClick} />
        {showWeaveVerifyPromotion && (
          <WivPromotionCard source='appointments' locationIds={[locationId]} cardStyles={wivPromotionCardStyles} />
        )}
      </div>
    );
  }

  if (!statusInfo) {
    return <InsuranceStatusChip statusInfo={unverifiedStatus} />;
  }

  return (
    <NakedButton css={{ width: 'fit-content' }} onClick={handleInsuranceStatusClick}>
      <InsuranceStatusChip statusInfo={statusInfo} />
    </NakedButton>
  );
};

type VerifyInsuranceButtonProps = {
  showWeaveVerify: boolean;
  onClick: () => void;
};

export const VerifyInsuranceButton = ({ showWeaveVerify, onClick }: VerifyInsuranceButtonProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  return (
    <NakedButton css={buttonStyle} disabled={!showWeaveVerify} onClick={onClick}>
      <Icon name='shield-small' css={{ minWidth: 'min-content', color: 'currentcolor' }} />
      {t('Verify')}
    </NakedButton>
  );
};

const buttonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontWeight: theme.font.weight.bold,
  fontSize: theme.fontSize(14),
  color: theme.colors.primary50,
  [':disabled']: {
    opacity: 0.5,
  },
});

const wivPromotionWrapperStyles = css({
  position: 'relative',
  ['div:last-child']: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [':hover div:last-child']: {
    opacity: 1,
    zIndex: 1,
    pointerEvents: 'auto',
  },
});

const wivPromotionCardStyles = css({
  position: 'absolute',
  left: 0,
  top: '100%',
  width: 'max-content',
  maxWidth: 382,
  boxShadow: theme.shadows.floating,
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(2),
  transition: 'opacity 0.25s ease-in-out',
});
